import React, { useEffect, useState, useContext } from "react";
import { getPosBatch, getProductMaster, get_completed_process_details } from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";
import { Pagination } from "../../Common/Functions";
import { getStoredEntriesPerPage, setStoredEntriesPerPage } from "../../../Utility/function";

const POBatchreport = () => {
    const { showLoader, hideLoader, shopItem } = useContext(WarehouseContext);

    const [summary, setSummary] = useState(null);
    const [batchData, setBatchData] = useState([]);
    const [products, setProducts] = useState([]);

    // Filter states
    const [selectedProduct, setSelectedProduct] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Modal detail state
    const [showModal, setShowModal] = useState(false);
    const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const [entriesPerPage, setEntriesPerPage] = useState(() => getStoredEntriesPerPage("pobatch_report", 10));
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch product list for product-wise filter dropdown
    const fetchProducts = async () => {
        try {
            const res = await getProductMaster({ shop_id: shopItem?.shop_id || "" });
            if (res?.data?.status === "success" || Array.isArray(res?.data?.data)) {
                setProducts(res.data.data || []);
            }
        } catch (err) {
            console.error("Error fetching products:", err);
        }
    };

    const fetchBatchReport = async () => {
        try {
            const params = {
                shop_id: shopItem?.shop_id || "",
            };
            if (selectedProduct) {
                params.product_id = selectedProduct;
            }
            if (fromDate) {
                params.from_date = fromDate;
            }
            if (toDate) {
                params.to_date = toDate;
            }

            showLoader();
            const res = await getPosBatch(params);
            if (res?.data?.status === "success") {
                setSummary(res.data.summary);
                setBatchData(res.data.data || []);
                setCurrentPage(1);
            }
        } catch (err) {
            console.error(err);
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [shopItem]);

    useEffect(() => {
        fetchBatchReport();
    }, [selectedProduct, fromDate, toDate]);

    const handleClearFilters = () => {
        setSelectedProduct("");
        setFromDate("");
        setToDate("");
    };

    const handleOpenDetailModal = async (item) => {
        setShowModal(true);
        setSelectedBatchDetails(item);
        if (item.processing_id) {
            setLoadingDetails(true);
            try {
                const res = await get_completed_process_details(item.processing_id);
                if (res?.data?.status === "success") {
                    setSelectedBatchDetails(prev => ({
                        ...prev,
                        full_details: res.data.data
                    }));
                }
            } catch (err) {
                console.error("Error fetching batch details:", err);
            } finally {
                setLoadingDetails(false);
            }
        }
    };

    const totalPages = Math.max(1, Math.ceil(batchData.length / entriesPerPage));

    const paginatedData = batchData.slice(
        (currentPage - 1) * entriesPerPage,
        currentPage * entriesPerPage
    );

    return (
        <div className="container-fluid p-4 bg-white min-vh-100">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="fw-bold mb-0 text-dark">PO Batch Analytics</h3>
                    <small className="text-muted">
                        Profit & loss breakdown by product batch
                    </small>
                </div>
                <span className="badge bg-dark px-3 py-2">
                    <i className="bi bi-graph-up me-1"></i> Live Report
                </span>
            </div>

            {/* FILTERS SECTION */}
            <div className="card border-0 shadow-sm mb-4 bg-light">
                <div className="card-body py-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-4">
                            <label className="form-label text-muted fw-bold mb-1 fs-7">Filter by Product</label>
                            <select
                                className="form-select form-select-sm border-secondary-subtle"
                                value={selectedProduct}
                                onChange={(e) => setSelectedProduct(e.target.value)}
                            >
                                <option value="">-- All Products --</option>
                                {products.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label text-muted fw-bold mb-1 fs-7">From Date</label>
                            <input
                                type="date"
                                className="form-control form-control-sm border-secondary-subtle"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label text-muted fw-bold mb-1 fs-7">To Date</label>
                            <input
                                type="date"
                                className="form-control form-control-sm border-secondary-subtle"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end" style={{ height: "100%" }}>
                            {(selectedProduct || fromDate || toDate) && (
                                <button
                                    className="btn btn-sm btn-outline-danger w-100 mt-4"
                                    onClick={handleClearFilters}
                                >
                                    <i className="bi bi-x-circle me-1"></i> Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* SUMMARY CARDS */}
            {summary && (
                <div className="row g-3 mb-4">
                    {[
                        {
                            title: "Manufacturing Cost",
                            value: summary.total_mfg_price,
                            icon: "bi-cash-stack",
                            bg: "white",
                            color: "text-dark",
                        },
                        {
                            title: "Total Profit",
                            value: summary.total_profit,
                            icon: "bi-arrow-up-circle",
                            bg: "success",
                            color: "text-white",
                        },
                        {
                            title: "Total Loss",
                            value: summary.total_loss,
                            icon: "bi-arrow-down-circle",
                            bg: "danger",
                            color: "text-white",
                        },
                    ].map((item, i) => (
                        <div className="col-xl-4 col-md-4" key={i}>
                            <div className={`card shadow-sm border-0 bg-${item.bg}`}>
                                <div className={`card-body ${item.color}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: "0.75rem" }}>{item.title}</small>
                                            <h4 className="fw-bold mt-1 mb-0">₹ {item.value}</h4>
                                        </div>
                                        <i className={`bi ${item.icon} fs-2 opacity-50`}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* BATCH TABLE */}
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white fw-bold py-3 border-bottom d-flex justify-content-between align-items-center">
                    <span><i className="bi bi-table me-2 text-primary"></i>Batch-wise Profit / Loss</span>
                    <span className="text-muted fw-normal fs-7">Total Records: {batchData.length}</span>
                </div>

                <div className="table-responsive">
                    <table className="table table-hover align-middle justify-content-center mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: "60px" }}>NO</th>
                                <th>Product</th>
                                <th>Batch No</th>
                                <th className="text-center">Product Qty</th>
                                <th className="text-center">Cost (₹)</th>
                                <th className="text-center">Sales (sold cost) (₹)</th>
                                <th className="text-center">Remaining</th>
                                <th className="text-center">Net Profit/Loss (₹)</th>
                                <th className="text-center" style={{ width: "90px" }}>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {batchData.length > 0 ? (
                                paginatedData.map((item, index) => {
                                    const netProfitLoss = item.net_result;
                                    return (
                                        <tr key={index}>
                                            <td className="fw-semibold text-muted text-center">
                                                {(currentPage - 1) * entriesPerPage + index + 1}
                                            </td>

                                            <td className="fw-semibold text-dark">
                                                {item.product_name || "N/A"}
                                            </td>

                                            <td className="fw-bold text-primary">
                                                {item.batch_no}
                                            </td>

                                            <td className="text-center fw-semibold">
                                                {item.product_qty} {item.unit || "kg"}
                                            </td>

                                            <td className="text-center fw-semibold text-secondary">
                                                ₹ {item.total_mfg_price || item.cost_amount || 0}
                                            </td>

                                            <td className="text-center fw-semibold text-dark">
                                                ₹ {item.sales_amount || 0}
                                            </td>

                                            <td className="text-center">
                                                <span className={`badge ${item.remaining_qty > 0 ? "bg-info text-dark" : "bg-light text-muted border"}`}>
                                                    {item.remaining_qty} {item.unit || "kg"}
                                                </span>
                                            </td>

                                            <td className="text-center fw-bold fs-6">
                                                {netProfitLoss > 0 ? (
                                                    <span className="text-success">
                                                        +₹ {netProfitLoss}
                                                    </span>
                                                ) : netProfitLoss < 0 ? (
                                                    <span className="text-danger">
                                                        -₹ {Math.abs(netProfitLoss)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">₹ 0</span>
                                                )}
                                            </td>

                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-outline-primary rounded-circle p-1 px-2"
                                                    title="View Details"
                                                    onClick={() => handleOpenDetailModal(item)}
                                                >
                                                    <i className="bi bi-info-circle-fill fs-6"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5 text-muted">
                                        <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary opacity-50"></i>
                                        No Batch Data Available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className="p-3 border-top">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            storageKey="pobatch_report"
                            onPageChange={(page) => setCurrentPage(page)}
                            entriesPerPage={entriesPerPage}
                            onEntriesPerPageChange={(val) => {
                                setStoredEntriesPerPage("pobatch_report", val);
                                setEntriesPerPage(val);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
            </div>
        </div>

            {/* BATCH DETAIL MODAL */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-dark text-white">
                                <h5 className="modal-title fw-bold">
                                    <i className="bi bi-card-heading me-2"></i>
                                    Batch Details: {selectedBatchDetails?.batch_no}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body p-4">
                                {loadingDetails ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-2 text-muted">Loading batch report details...</p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* HEADER INFO */}
                                        <div className="bg-light p-3 rounded mb-4 border">
                                            <div className="row g-2">
                                                <div className="col-md-6">
                                                    <strong>Product: </strong> {selectedBatchDetails?.product_name || "N/A"}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Batch No: </strong> <span className="badge bg-primary fs-7">{selectedBatchDetails?.batch_no}</span>
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Manufactured Qty: </strong> {selectedBatchDetails?.product_qty} {selectedBatchDetails?.unit || "kg"}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Remaining Bulk Stock: </strong> {selectedBatchDetails?.remaining_qty} {selectedBatchDetails?.unit || "kg"}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Manufacturing Cost: </strong> ₹ {selectedBatchDetails?.total_mfg_price || 0}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Sales Revenue: </strong> ₹ {selectedBatchDetails?.sales_amount || 0}
                                                </div>
                                            </div>
                                        </div>

                                        {/* RAW MATERIAL REPORT */}
                                        <div className="mb-4">
                                            <h6 className="fw-bold border-bottom pb-2 text-primary">
                                                <i className="bi bi-box-seam me-2"></i>1. Raw Material Report
                                            </h6>
                                            {selectedBatchDetails?.full_details?.ingredients?.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered align-middle">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Raw Material</th>
                                                                <th className="text-center">Qty Used</th>
                                                                <th className="text-center">Unit</th>
                                                                <th className="text-end">Cost (₹)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedBatchDetails.full_details.ingredients.map((ing, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{ing.raw_material_name}</td>
                                                                    <td className="text-center">{ing.qty}</td>
                                                                    <td className="text-center">{ing.unit}</td>
                                                                    <td className="text-end">₹ {ing.price || 0}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p className="text-muted small italic">No raw material details found for this batch.</p>
                                            )}
                                        </div>

                                        {/* EXTRA CHARGES & MANUFACTURING EXPENSES */}
                                        <div className="mb-4">
                                            <h6 className="fw-bold border-bottom pb-2 text-success">
                                                <i className="bi bi-receipt me-2"></i>2. Extra Charges & Manufacturing Expenses
                                            </h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-bordered align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Charge Name / Description</th>
                                                            <th className="text-end">Amount (₹)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>Labour Charges</td>
                                                            <td className="text-end">₹ {Number(selectedBatchDetails?.full_details?.labour_charges || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>EB / Rent Charges</td>
                                                            <td className="text-end">₹ {Number(selectedBatchDetails?.full_details?.eb_rent_charges || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Packaging Charges (Cash)</td>
                                                            <td className="text-end">₹ {Number(selectedBatchDetails?.full_details?.package_charges || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Other Expenses</td>
                                                            <td className="text-end">₹ {Number(selectedBatchDetails?.full_details?.other_charges || 0).toFixed(2)}</td>
                                                        </tr>
                                                        {selectedBatchDetails?.full_details?.custom_expenses?.map((cExp, idx) => (
                                                            <tr key={idx}>
                                                                <td>{cExp.title}</td>
                                                                <td className="text-end">₹ {Number(cExp.amount || 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* PACKAGING COST & BATCH DISTRIBUTION */}
                                        <div>
                                            <h6 className="fw-bold border-bottom pb-2 text-dark">
                                                <i className="bi bi-boxes me-2"></i>3. Packaging Cost & Distributed Batches
                                            </h6>
                                            {selectedBatchDetails?.full_details?.distribution?.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered align-middle">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Package Description</th>
                                                                <th className="text-center">Packets Produced</th>
                                                                <th className="text-end">Unit Mfg Cost (₹)</th>
                                                                <th className="text-end">Total Cost (₹)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedBatchDetails.full_details.distribution.map((dist, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{dist.packet_title}</td>
                                                                    <td className="text-center">{dist.qty}</td>
                                                                    <td className="text-end">₹ {dist.price || 0}</td>
                                                                    <td className="text-end fw-semibold">₹ {((dist.qty || 0) * (dist.price || 0)).toFixed(2)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p className="text-muted small italic">No distributed packet details available for this batch.</p>
                                            )}
                                        </div>

                                    </div>
                                )}
                            </div>

                            <div className="modal-footer bg-light">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default POBatchreport;
